import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Text,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useTeamService } from '@/hooks/useTeamService';

interface InviteFormData {
  email: string;
  username: string;
  role: string;
  message: string;
}

interface TeamInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  onSuccess: () => void;
}

export const TeamInviteModal: React.FC<TeamInviteModalProps> = ({
  isOpen,
  onClose,
  teamId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const teamService = useTeamService();
  const textColor = useColorModeValue('gray.600', 'gray.400');
  
  const [inviteData, setInviteData] = useState<InviteFormData>({
    email: '',
    username: '',
    role: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInviteUser = async () => {
    setIsSubmitting(true);
    try {
      await teamService.inviteUserToTeam(teamId, inviteData);
      toast({ title: t('teams.invitationSent'), status: 'success' });
      onClose();
      setInviteData({ email: '', username: '', role: '', message: '' });
      onSuccess();
    } catch (err: any) {
      toast({ title: t('common.error'), description: err.message, status: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('teams.inviteUser')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>{t('common.email')}</FormLabel>
              <Input
                type="email"
                value={inviteData.email}
                onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="user@example.com"
              />
            </FormControl>
            
            <Text textAlign="center" color={textColor}>{t('common.or')}</Text>
            
            <FormControl>
              <FormLabel>{t('common.username')}</FormLabel>
              <Input
                value={inviteData.username}
                onChange={(e) => setInviteData(prev => ({ ...prev, username: e.target.value }))}
                placeholder="username"
              />
            </FormControl>
            
            <FormControl>
              <FormLabel>{t('teams.roleOptional')}</FormLabel>
              <Select
                value={inviteData.role}
                onChange={(e) => setInviteData(prev => ({ ...prev, role: e.target.value }))}
                placeholder={t('teams.selectRole')}
              >
                <option value="member">{t('common.member')}</option>
                <option value="admin">{t('common.admin')}</option>
                <option value="moderator">{t('common.moderator')}</option>
              </Select>
            </FormControl>
            
            <FormControl>
              <FormLabel>{t('teams.messageOptional')}</FormLabel>
              <Textarea
                value={inviteData.message}
                onChange={(e) => setInviteData(prev => ({ ...prev, message: e.target.value }))}
                placeholder={t('teams.personalMessagePlaceholder')}
                rows={3}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleInviteUser}
            isLoading={isSubmitting}
          >
            {t('teams.sendInvitation')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}; 